/**
 * A tiny 5x7 bitmap font drawn straight onto the canvas.
 * Keeping the font in code means the playfield renders identically offline and
 * never waits on a webfont, which matters for the arcade look.
 */

const GLYPHS = {
  A: '01110,10001,10001,11111,10001,10001,10001',
  B: '11110,10001,10001,11110,10001,10001,11110',
  C: '01110,10001,10000,10000,10000,10001,01110',
  D: '11110,10001,10001,10001,10001,10001,11110',
  E: '11111,10000,10000,11110,10000,10000,11111',
  F: '11111,10000,10000,11110,10000,10000,10000',
  G: '01110,10001,10000,10111,10001,10001,01111',
  H: '10001,10001,10001,11111,10001,10001,10001',
  I: '01110,00100,00100,00100,00100,00100,01110',
  J: '00111,00010,00010,00010,00010,10010,01100',
  K: '10001,10010,10100,11000,10100,10010,10001',
  L: '10000,10000,10000,10000,10000,10000,11111',
  M: '10001,11011,10101,10101,10001,10001,10001',
  N: '10001,11001,10101,10011,10001,10001,10001',
  O: '01110,10001,10001,10001,10001,10001,01110',
  P: '11110,10001,10001,11110,10000,10000,10000',
  Q: '01110,10001,10001,10001,10101,10010,01101',
  R: '11110,10001,10001,11110,10100,10010,10001',
  S: '01111,10000,10000,01110,00001,00001,11110',
  T: '11111,00100,00100,00100,00100,00100,00100',
  U: '10001,10001,10001,10001,10001,10001,01110',
  V: '10001,10001,10001,10001,10001,01010,00100',
  W: '10001,10001,10001,10101,10101,11011,10001',
  X: '10001,10001,01010,00100,01010,10001,10001',
  Y: '10001,10001,01010,00100,00100,00100,00100',
  Z: '11111,00001,00010,00100,01000,10000,11111',
  0: '01110,10001,10011,10101,11001,10001,01110',
  1: '00100,01100,00100,00100,00100,00100,01110',
  2: '01110,10001,00001,00010,00100,01000,11111',
  3: '11111,00010,00100,00010,00001,10001,01110',
  4: '00010,00110,01010,10010,11111,00010,00010',
  5: '11111,10000,11110,00001,00001,10001,01110',
  6: '00110,01000,10000,11110,10001,10001,01110',
  7: '11111,00001,00010,00100,01000,01000,01000',
  8: '01110,10001,10001,01110,10001,10001,01110',
  9: '01110,10001,10001,01111,00001,00010,01100',
  ' ': '00000,00000,00000,00000,00000,00000,00000',
  '.': '00000,00000,00000,00000,00000,01100,01100',
  ',': '00000,00000,00000,00000,01100,01100,01000',
  '!': '00100,00100,00100,00100,00100,00000,00100',
  '?': '01110,10001,00001,00010,00100,00000,00100',
  '-': '00000,00000,00000,11111,00000,00000,00000',
  ':': '00000,01100,01100,00000,01100,01100,00000',
  "'": '00100,00100,00000,00000,00000,00000,00000',
  '/': '00001,00010,00010,00100,01000,01000,10000',
  '+': '00000,00100,00100,11111,00100,00100,00000',
  '*': '00000,10101,01110,11111,01110,10101,00000',
  '<': '00010,00100,01000,10000,01000,00100,00010',
  '>': '01000,00100,00010,00001,00010,00100,01000',
  '(': '00010,00100,01000,01000,01000,00100,00010',
  ')': '01000,00100,00010,00010,00010,00100,01000',
  '%': '11001,11010,00010,00100,01000,01011,10011',
  '=': '00000,00000,11111,00000,11111,00000,00000',
  '#': '01010,11111,01010,01010,01010,11111,01010',
};

export const GLYPH_W = 5;
export const GLYPH_H = 7;

/** Pre-parsed glyph masks: each entry is an array of 7 row bitmasks. */
const MASKS = new Map();
for (const [char, rows] of Object.entries(GLYPHS)) {
  MASKS.set(
    char,
    rows.split(',').map((row) => parseInt(row, 2)),
  );
}

const FALLBACK = MASKS.get('?');

/**
 * Width in logical pixels of a string at the given pixel scale.
 * @param {string} text
 * @param {number} scale size of one font pixel
 * @param {number} tracking extra gap between glyphs, in font pixels
 */
export function measureText(text, scale = 2, tracking = 1) {
  if (!text.length) return 0;
  return (text.length * (GLYPH_W + tracking) - tracking) * scale;
}

export function textHeight(scale = 2) {
  return GLYPH_H * scale;
}

/**
 * Draw uppercase-normalised bitmap text.
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x left edge (or centre when align is 'center')
 * @param {number} y top edge
 * @param {object} [opts]
 */
export function drawText(ctx, text, x, y, opts = {}) {
  const {
    scale = 2,
    color = '#ffffff',
    align = 'left',
    tracking = 1,
    shadow = null,
  } = opts;

  const chars = String(text).toUpperCase();
  const width = measureText(chars, scale, tracking);
  let originX = x;
  if (align === 'center') originX = Math.round(x - width / 2);
  else if (align === 'right') originX = Math.round(x - width);

  if (shadow) paintText(ctx, chars, originX + scale, y + scale, scale, tracking, shadow);
  paintText(ctx, chars, originX, y, scale, tracking, color);
  return width;
}

function paintText(ctx, chars, x, y, scale, tracking, color) {
  ctx.fillStyle = color;
  const advance = (GLYPH_W + tracking) * scale;
  for (let i = 0; i < chars.length; i += 1) {
    const mask = MASKS.get(chars[i]) || FALLBACK;
    const gx = x + i * advance;
    for (let row = 0; row < GLYPH_H; row += 1) {
      const bits = mask[row];
      if (!bits) continue;
      for (let col = 0; col < GLYPH_W; col += 1) {
        if (bits & (1 << (GLYPH_W - 1 - col))) {
          ctx.fillRect(gx + col * scale, y + row * scale, scale, scale);
        }
      }
    }
  }
}
